'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Whether the surface under a bar control is the page's opposite (the black quiz card on
 * a light page, a white answer tile on a dark one), read from the page itself as it
 * scrolls, so no card has to be tagged. Five points across the control vote, so one that
 * straddles a card's edge goes with whatever covers most of it. A photo or a video has no
 * background and casts no vote.
 */
export function useOppositeGround<T extends Element>() {
  const ref = useRef<T>(null);
  const [opposite, setOpposite] = useState(false);

  useEffect(() => {
    let frame = 0;
    const read = () => {
      frame = 0;
      const el = ref.current;
      if (!el) return;
      const ground = luminance(getComputedStyle(document.body).backgroundColor);
      if (ground === null) return;
      const r = el.getBoundingClientRect();
      const votes = POINTS.map(([x, y]) => {
        const under = document
          .elementsFromPoint(r.left + r.width * x, r.top + r.height * y)
          .find((n) => !n.closest('[data-site-bar]'));
        const surface = under ? surfaceLuminance(under) : null;
        return surface !== null && Math.abs(surface - ground) > 0.5;
      });
      setOpposite(votes.filter(Boolean).length >= 3);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(read);
    };
    read();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, []);

  return [ref, opposite] as const;
}

/** The centre and four points a quarter in from each corner, as fractions of the box. */
const POINTS = [
  [0.5, 0.5],
  [0.25, 0.25],
  [0.75, 0.25],
  [0.25, 0.75],
  [0.75, 0.75],
] as const;

/** The first mostly-opaque background at or above `node`; null under a photo or a video. */
function surfaceLuminance(node: Element): number | null {
  for (let n: Element | null = node; n; n = n.parentElement) {
    if (n instanceof HTMLImageElement || n instanceof HTMLVideoElement) return null;
    const bg = parseColor(getComputedStyle(n).backgroundColor);
    if (bg && bg.a >= 0.5) return bg.l;
  }
  return null;
}

function luminance(css: string): number | null {
  return parseColor(css)?.l ?? null;
}

/** `rgb()`/`rgba()` in 0-255, or `color(srgb …)` in 0-1, as browsers compute them. */
function parseColor(css: string): { l: number; a: number } | null {
  const n = css.match(/[\d.]+/g)?.map(Number);
  if (!n || n.length < 3) return null;
  const unit = css.startsWith('color(') ? 1 : 255;
  const [r, g, b] = n.slice(0, 3).map((v) => v / unit);
  return { l: 0.2126 * r + 0.7152 * g + 0.0722 * b, a: n[3] ?? 1 };
}
